// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Send to an agent" from a message, an email or a ClickUp task. The item rides to the new
// agent chat page as context (quoted under the prompt when it starts); the machine and project
// are guessed from where it came from (the channel, the ClickUp list, the sender's domain), else
// the page's own default. Outside Agents it opens a small panel over where the person is; inside
// Agents the full new chat page. Results come back to the agent thread and Home only.

import Agents from '@app/features/konvo_agents/state/Agents';
import AgentsAccess from '@app/features/konvo_agents/state/AgentsAccess';
import {type AgentChatContext, canSend, guessProject} from '@app/features/konvo_agents/state/AgentsModel';
import Navigation from '@app/features/navigation/state/Navigation';

/** Whether the menus offer it: Agents is on for the person. A machine is checked on the page. */
export function canSendToAgent(): boolean {
	return AgentsAccess.enabled;
}

export async function sendToAgent(context: AgentChatContext): Promise<void> {
	if (!Agents.loaded) await Agents.loadOverview().catch(() => undefined);
	const machines = Agents.machines.filter((machine) => canSend(machine));
	// The machine the list shows first, then the others.
	const ordered = [
		...machines.filter((machine) => machine.id === Agents.listMachineId),
		...machines.filter((machine) => machine.id !== Agents.listMachineId),
	];
	let found: {machineId: string; projectId: string; hint: string} | null = null;
	for (const machine of ordered) {
		if (!Agents.list(machine.id)?.threads) await Agents.loadThreads(machine.id).catch(() => undefined);
		const guess = guessProject(Agents.projectsOf(machine.id), context.hints);
		if (guess) {
			found = {machineId: machine.id, ...guess};
			break;
		}
	}
	Agents.setNewChatContext(context, found?.hint ?? null);
	// Inside Agents the full page; anywhere else a small panel, so the person keeps their place.
	// (The Agents routes, matched here so the menus that offer this stay light to import.)
	if (/^\/agents(\/|$)/.test(window.location.pathname)) {
		Navigation.navigateToNewAgentChat(found?.machineId ?? ordered[0]?.id, found?.projectId);
		return;
	}
	const {openSendToAgentPanel} = await import('@app/features/konvo_agents/components/AgentSendPanel');
	openSendToAgentPanel(found ? {machineId: found.machineId, projectId: found.projectId} : null);
}
